/*
 * "ALONG THE BOTTOM BAR" > SAILBOATS: two small sailboats far out, with the bottom metrics bar as the sea.
 *
 * A port of design-prototypes/empty-state-motion/src/sailboat/sailboat.js (the mock Chris approved to build after asking for
 * it smaller, with less linear movement and the lighter gray). If you change how a boat LOOKS or MOVES, change the mock too
 * (and republish its sheet): they are two copies of one drawing. The differences the real page needs:
 *   1. The mock lives in a fixed 1000-unit stage. Here the stage is whatever width the real footer is. A boat's SIZE follows the
 *      page (u0, from 0.8 to 1.6, the same rule as the fisherman) and its ROUTE spans the whole footer: a route is written in
 *      0..1000 route units and `xs` stretches it to the stage's own width.
 *   2. It is drawn into a canvas laid over the top edge of the footer (ambientLife.ts owns that canvas, its fade and its
 *      schedule), not into a replica.
 *
 * WHAT MAKES A BOAT MOVE LIKE A BOAT (all of it a pure function of time, so any moment can be drawn with no loop or state):
 *   1. A ROUTE, rolled from a seed: straight across, or in to a point and TURNED AROUND to leave the way it came, or in, back
 *      a bit and on across (a zigzag, which is how a sailboat works upwind). Each visit is a different route.
 *   2. GUSTS: its speed rises and falls on two slow, unrelated beats, so it never moves at one pace for long.
 *   3. A TACK: it slows into the turn, comes almost to a stop and TURNS (seen from the side a boat turning is foreshortened: it
 *      narrows to a sliver and widens the other way), then picks up again. The sails go slack while it is slow (flutter, flat)
 *      and fill as it speeds up (belly), and the wake and bow wave come and go with its speed.
 *   4. The swell: heave and pitch from three unrelated sines, and the bow lifts a little with speed.
 *
 * ONE FLAT COLOUR, no outline: the lighter slate #b9c4cc for both boats (the farther one is only smaller and slower). Where one
 * boat passes in front of the other a thin gap is cut round the nearer one so they never merge into one blob.
 *
 * NOTHING IS EVER DRAWN OVER A NUMBER: the boats float ABOVE the footer's top edge, which is the surface of the sea, and the
 * whole scene is clipped to a band that ends `maxBelow` units under that edge (the caller sets it from the real gap between the
 * edge and the first line of text, as for the fisherman). Measured in the mock: with the clip widened to 40 units the deepest
 * pixel the boats ever paint is 5.8 units under the edge, so the clip is a backstop here, not what keeps them off the numbers.
 *
 * Stage units: the sea surface is y = SAILBOATS_SURF (the footer's top edge). Boat frame: origin = the middle of the hull at the
 * waterline, +x = toward the bow, y up is negative, drawn in units of the hull length L. Route frame: p runs 0..1000 in the
 * direction the boat first travels (it enters at p = -1.2 L), then x = p, or 1000 - p for a boat from the right.
 */
import { drawBarSea } from "./ambientFisherman";

export const SAILBOATS_SURF = 533;
/** how far above the surface the canvas must reach (the masthead, the pennant, the swell), and how far below (the reflection) */
export const SAILBOATS_ABOVE = 48;
export const SAILBOATS_BELOW = 10;
const BARB = 572;                          // the bottom of the bar in the replica; only the top few units are ever drawn on
const SURF = SAILBOATS_SURF;
export const SAILBOAT_GREY = "#b9c4cc";
const NEAR_L = 30, FAR_L = 19;             // hull lengths in stage units (the first mock-up was 50 and 31)
const NEAR_V = 0.62, FAR_V = 0.58;         // cruising speed, in hull lengths a second: a farther boat covers less ground
const TAU = 6.2832;
const MIN_WIDTH = 560;

const clamp = (x: number, lo = 0, hi = 1) => Math.min(hi, Math.max(lo, x));
const ease = (k: number) => { const c = clamp(k); return c * c * (3 - 2 * c); };
const rng = (seed: number) => () => { seed = (seed + 0x6d2b79f5) | 0; let t = Math.imul(seed ^ (seed >>> 15), 1 | seed); t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t; return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };

/* ───────── where they fit ─────────
   A footer under MIN_WIDTH has no room for a sea at all. Boats are sized by the page's own scale, as the fisherman is. */
export interface SailboatsPlan { u: number; width: number }   // u: stage units to CSS pixels; width in stage units
export function sailboatsPlan(width: number): SailboatsPlan | null {
  if (!(width >= MIN_WIDTH)) return null;
  const u = clamp(width / 1000, 0.8, 1.6);
  return { u, width: width / u };
}

/* ───────── the route ───────── */
interface Seg { turn: boolean; t0: number; t1: number; p0: number; p1: number; mode: "none" | "in" | "out" | "both"; a: number; f0: number }
export type RouteKind = "cross" | "turn" | "zig";
export interface Boat {
  seed: number; kind: RouteKind; dir: 1 | -1; t0: number; L: number; v: number; color: string; ph: number;
  segs: Seg[]; total: number; T1: number; f1: number; T2: number; f2: number; g1: number; g2: number; endRel: number;
}
export interface BoatOptions { seed: number; dir: 1 | -1; t0: number; L: number; v: number; route?: RouteKind; color?: string }

function tauOf(b: Boat, rel: number): number {
  return rel + b.g1 * (Math.sin(TAU * rel / b.T1 + b.f1) - Math.sin(b.f1)) + b.g2 * (Math.sin(TAU * rel / b.T2 + b.f2) - Math.sin(b.f2));
}
function relOfTau(b: Boat, tau: number): number {
  let lo = 0, hi = tau / 0.45 + 2;
  for (let k = 0; k < 40; k++) { const m = (lo + hi) / 2; if (tauOf(b, m) < tau) lo = m; else hi = m; }
  return (lo + hi) / 2;
}
export function makeBoat(o: BoatOptions): Boat {
  const r = rng(o.seed * 7919 + 17), L = o.L, v = o.v, p0 = -1.2 * L, pEnd = 1000 + 1.2 * L;
  const kind: RouteKind = o.route || ((): RouteKind => { const u = r(); return u < 0.22 ? "cross" : u < 0.5 ? "turn" : "zig"; })();
  const way = [p0], f0 = 300 + r() * 420, b0 = Math.max(80, f0 - (140 + r() * 200));
  if (kind === "cross") way.push(pEnd);
  else if (kind === "turn") way.push(f0, p0);                                  // in to a point, turn round, leave the way it came
  else way.push(f0, b0, pEnd);                                                 // in, back a bit, on across
  const segs: Seg[] = [];
  let tc = 0;
  for (let i = 0; i < way.length - 1; i++) {
    const a = way[i], b = way[i + 1], d = Math.abs(b - a), lf = 0.85 + r() * 0.3, T = d / (v * lf);
    const inE = i > 0, outE = i < way.length - 2;
    segs.push({ turn: false, t0: tc, t1: tc + T, p0: a, p1: b, mode: inE && outE ? "both" : outE ? "out" : inE ? "in" : "none", a: i === 0 ? 0.4 : 0.65, f0: 0 });
    tc += T;
    if (outE) { const Tt = 3.4 + r() * 1.8; segs.push({ turn: true, t0: tc, t1: tc + Tt, p0: b, p1: b, mode: "none", a: 0, f0: b > a ? 1 : -1 }); tc += Tt; }
  }
  const B: Boat = {
    seed: o.seed, kind, dir: o.dir, t0: o.t0, L, v, color: o.color || SAILBOAT_GREY, ph: r() * TAU, segs, total: tc,
    T1: 8 + r() * 7, f1: r() * TAU, T2: 3.5 + r() * 2.5, f2: r() * TAU, g1: 0, g2: 0, endRel: 0,
  };
  B.g1 = 0.28 * B.T1 / TAU; B.g2 = 0.1 * B.T2 / TAU;                          // the gusts: the pace swings between about 0.62 and 1.38 of its mean
  B.endRel = relOfTau(B, tc);
  return B;
}
function shape(u: number, mode: Seg["mode"], a: number): number {
  if (mode === "both") return (1 - a) * u + a * u * u * (3 - 2 * u);
  if (mode === "out") return (1 - a) * u + a * (1 - (1 - u) * (1 - u));
  if (mode === "in") return (1 - a) * u + a * u * u;
  return u;
}
function placeAt(b: Boat, tau: number): { p: number; face: number } {         // where on its route, and which way it faces (+1 forward, -1 back, between while turning)
  const S = b.segs;
  let s = S[S.length - 1];
  for (let i = 0; i < S.length; i++) if (tau <= S[i].t1) { s = S[i]; break; }
  const u = clamp((tau - s.t0) / (s.t1 - s.t0));
  if (s.turn) {
    let f = s.f0 * Math.cos(Math.PI * ease(u));
    if (Math.abs(f) < 0.1) f = f < 0 || (f === 0 && s.f0 < 0) ? -0.1 : 0.1;
    return { p: s.p0, face: f };
  }
  return { p: s.p0 + (s.p1 - s.p0) * shape(u, s.mode, s.a), face: s.p1 > s.p0 ? 1 : -1 };
}
export interface Pose { x: number; face: number; vx: number; sf: number }
/** where a boat is at time t (seconds since the visit began), or null when it is not out */
export function sailboatPose(b: Boat, t: number): Pose | null {
  const rel = t - b.t0;
  if (rel < 0 || rel > b.endRel) return null;
  const s = placeAt(b, tauOf(b, rel)), s2 = placeAt(b, tauOf(b, rel + 0.08)), s1 = placeAt(b, tauOf(b, Math.max(0, rel - 0.08)));
  const vx = b.dir * (s2.p - s1.p) / 0.16;
  return { x: b.dir > 0 ? s.p : 1000 - s.p, face: b.dir * s.face, vx, sf: Math.abs(vx) / b.v };
}

/**
 * A visit: TWO boats, nearest first ("ship the 2 boats", Chris). The seed decides the routes, the side each comes from (they start
 * on opposite sides), and when the nearer one comes in: the farther one is already out. Seconds are since the visit began.
 */
export interface SailVisit { boats: Boat[]; end: number }
export function makeSailVisit(seed: number): SailVisit {
  const r = rng(seed * 131 + 5), d: 1 | -1 = r() < 0.5 ? 1 : -1, nl = NEAR_L * (0.94 + 0.12 * r());
  const near = makeBoat({ seed: seed * 2 + 1, dir: d, t0: 5 + r() * 9, L: nl, v: NEAR_V * nl });
  const fl = FAR_L * (0.94 + 0.12 * r());
  const far = makeBoat({ seed: seed * 2 + 2, dir: (-d) as 1 | -1, t0: 1, L: fl, v: FAR_V * fl });
  const boats = [near, far];
  return { boats, end: boats.reduce((m, b) => Math.max(m, b.t0 + b.endRel), 0) + 1.5 };
}

/* ───────── one boat ───────── */
function hullPath(ctx: CanvasRenderingContext2D) {
  ctx.beginPath();
  ctx.moveTo(0.5, -0.06);                                   // the bow's tip, a little high
  ctx.bezierCurveTo(0.3, -0.04, 0.1, -0.035, -0.08, -0.034);
  ctx.bezierCurveTo(-0.3, -0.035, -0.42, -0.05, -0.5, -0.066);   // the sheer line, up to the stern
  ctx.lineTo(-0.485, -0.004);                               // the transom
  ctx.bezierCurveTo(-0.3, 0.02, 0.1, 0.03, 0.34, 0.012);   // the bottom: shallow, and partly clipped away
  ctx.bezierCurveTo(0.44, 0.0, 0.48, -0.03, 0.5, -0.06);
  ctx.closePath();
}
function jibPath(ctx: CanvasRenderingContext2D, belly: number, fl: number) {
  ctx.beginPath(); ctx.moveTo(0.49, -0.064);
  ctx.quadraticCurveTo(0.32 + fl * 2, -0.45, 0.108, -0.82);                // the luff, along the forestay, with a little sag
  ctx.quadraticCurveTo(0.082 + belly * 0.6 + fl * 3, -0.42, 0.13, -0.1);   // the leech
  ctx.quadraticCurveTo(0.3, -0.077 - belly * 0.25, 0.49, -0.064);          // the foot
  ctx.closePath();
}
function mainPath(ctx: CanvasRenderingContext2D, belly: number, fl: number) {
  ctx.beginPath(); ctx.moveTo(0.062, -0.06);
  ctx.lineTo(0.058, -0.93);
  ctx.quadraticCurveTo(-0.2 - belly * 1.7 + fl * 4, -0.55, -0.41 + fl * 2, -0.088);   // the leech, bellying out behind
  ctx.quadraticCurveTo(-0.15, -0.048 - belly * 0.35, 0.062, -0.06);                  // the foot
  ctx.closePath();
}
function drawBoat(ctx: CanvasRenderingContext2D, b: Boat, P: Pose, X: number, t: number, knock: boolean) {
  const L = b.L, ph = b.ph, sf = clamp(P.sf, 0, 1.4), luff = clamp(1 - sf / 0.45);            // luff: 1 when almost stopped, the sails slack
  // the swell lifts and tips it a little (three unrelated beats), and the bow lifts with speed
  const heave = (0.3 * Math.sin(t * 0.9 + ph) + 0.14 * Math.sin(t * 1.7 + ph * 2) + 0.08 * Math.sin(t * 2.9 + ph * 3.1)) * (L / 25);
  const pitch = 0.024 * Math.sin(t * 0.7 + ph + 1) + 0.012 * Math.sin(t * 1.9 + ph * 2) - 0.03 * sf;
  const belly = (0.012 + 0.036 * clamp(sf, 0, 1.2)) * (1 - 0.6 * luff) + 0.007 * Math.sin(t * 1.3 + ph);   // the sails fill with speed
  const fl = (0.003 + 0.03 * luff) * Math.sin(t * (5.2 + 3 * luff) + ph) + 0.012 * luff * Math.sin(t * 3.1 + ph * 2);   // and flutter when it is slow
  ctx.save();
  ctx.translate(X, SURF + heave); ctx.scale(P.face * L, L); ctx.rotate(pitch);
  ctx.fillStyle = b.color; ctx.strokeStyle = b.color; ctx.lineCap = "round"; ctx.lineJoin = "round";
  const px = 1 / L, mx = 0.09, mTop = [0.076, -0.97];
  if (knock) {   // a thin gap round the nearer boat, cut out of whatever is behind it, so two boats of one colour stay two boats
    ctx.save(); ctx.globalCompositeOperation = "destination-out"; ctx.lineWidth = 1.5 * px;
    hullPath(ctx); ctx.fill(); ctx.stroke(); mainPath(ctx, belly, fl); ctx.fill(); ctx.stroke(); jibPath(ctx, belly, fl); ctx.fill(); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(mx, -0.035); ctx.lineTo(mTop[0], mTop[1]); ctx.lineWidth = 2.2 * px; ctx.stroke();
    ctx.restore();
  }
  hullPath(ctx); ctx.fill();
  // the mast, raked back a little, with a tiny pennant at the masthead
  ctx.lineWidth = Math.max(0.014, px * 0.8); ctx.beginPath(); ctx.moveTo(mx, -0.035); ctx.lineTo(mTop[0], mTop[1]); ctx.stroke();
  ctx.beginPath(); ctx.moveTo(mTop[0], mTop[1]); ctx.lineTo(mTop[0] - 0.09 - fl * 6, mTop[1] + 0.012 + fl * 3); ctx.lineTo(mTop[0], mTop[1] + 0.03); ctx.closePath(); ctx.fill();
  // the jib, forward of the mast, and the mainsail, aft of it: a clear gap each side of the mast keeps them two sails
  mainPath(ctx, belly, fl); ctx.fill(); jibPath(ctx, belly, fl); ctx.fill();
  ctx.lineWidth = Math.max(0.01, px * 0.6); ctx.beginPath(); ctx.moveTo(mx, -0.062); ctx.lineTo(-0.41 + fl * 2, -0.088); ctx.stroke();
  // turned nearly end-on (mid-tack) the hull and sails are slivers: keep the mast as a thin line so it still reads as a boat
  const fa = Math.abs(P.face);
  if (fa < 0.4) { const w = 0.8 / (L * Math.max(fa, 0.1)); ctx.globalAlpha = 1 - fa / 0.4; ctx.fillRect(mx - w / 2, mTop[1], w, -mTop[1] - 0.03); }
  ctx.restore();
}

/** the bow wave and the wake: thin pale lines on the sea's own surface, only while the boat is moving */
function drawWake(ctx: CanvasRenderingContext2D, b: Boat, P: Pose, X: number, t: number, a: number) {
  const m = clamp(Math.abs(P.vx) / (b.v * 0.7));
  if (m < 0.05) return;
  const L = b.L, d = P.vx > 0 ? 1 : -1, y = SURF;
  a *= m;
  ctx.save(); ctx.lineCap = "round";
  ([[0.95, 1.4, 0.34], [0.6, 2.7, 0.2]] as number[][]).forEach((w) => {
    const x0 = X - d * L * 0.5, x1 = X - d * L * (0.5 + w[0]), g = ctx.createLinearGradient(x0, 0, x1, 0);
    g.addColorStop(0, `rgba(255,255,255,${w[2] * a})`); g.addColorStop(1, "rgba(255,255,255,0)");
    ctx.strokeStyle = g; ctx.lineWidth = 0.55; ctx.beginPath(); ctx.moveTo(x0, y + w[1]); ctx.lineTo(x1, y + w[1] + 0.2 * Math.sin(t * 2 + x0 * 0.1)); ctx.stroke();
  });
  ctx.strokeStyle = `rgba(255,255,255,${0.5 * a})`; ctx.lineWidth = 0.6;
  ctx.beginPath(); ctx.ellipse(X + d * L * 0.5 * Math.abs(P.face), y + 0.8, L * 0.05, 0.9, 0, 0.1, Math.PI - 0.1); ctx.stroke();
  ctx.restore();
}

let layer: HTMLCanvasElement | null = null;
function layerFor(w: number, h: number): HTMLCanvasElement {
  if (!layer) layer = document.createElement("canvas");
  if (layer.width !== w || layer.height !== h) { layer.width = w; layer.height = h; }
  return layer;
}

export interface SailboatsDrawOptions {
  boats: Boat[];
  /** 0 to 1: the whole scene's own fade */
  alpha: number;
  /** the canvas's own pixel size, for the reflection layer */
  cw: number; ch: number;
  /** the horizontal extent to draw the sea over, in stage units */
  x0: number; x1: number;
  /** nothing is drawn deeper than this many stage units below the surface: the gap above the first line of text, less a margin */
  maxBelow: number;
  /** route units to stage units along the page: stage width / 1000 (a route is written in 0..1000) */
  xs: number;
}

/**
 * Draws one moment of the scene. `ctx` must already be set so that one stage unit is one unit of the context, with y = SURF on
 * the footer's top edge. `t` is seconds since the visit began.
 */
export function drawSailboats(ctx: CanvasRenderingContext2D, t: number, o: SailboatsDrawOptions) {
  const a = clamp(o.alpha);
  if (a <= 0.003) return;
  // THE GUARANTEE THAT NOTHING REACHES A NUMBER: the whole scene is clipped to a band that ends maxBelow units under the
  // footer's top edge. The clip is in device space, so the context's later resets of its transform do not undo it.
  ctx.save();
  ctx.beginPath(); ctx.rect(o.x0, SURF - 1000, o.x1 - o.x0, 1000 + Math.max(0, o.maxBelow)); ctx.clip();
  drawBarSea(ctx, t, a, o.x0, o.x1);
  const tf = ctx.getTransform(), L = layerFor(o.cw, o.ch), lc = L.getContext("2d");
  if (!lc) { ctx.restore(); return; }
  lc.setTransform(1, 0, 0, 1, 0, 0); lc.clearRect(0, 0, L.width, L.height); lc.setTransform(tf);
  const live: { b: Boat; P: Pose; X: number }[] = [];
  o.boats.forEach((b) => { const P = sailboatPose(b, t); if (P) live.push({ b, P, X: P.x * o.xs }); });
  // farther (smaller) boats first, so a nearer one is drawn over a farther one if they cross
  live.sort((p, q) => p.b.L - q.b.L);
  live.forEach((e, i) => {
    const knock = live.some((o2, j) => j < i && Math.abs(o2.X - e.X) < (o2.b.L + e.b.L) * 0.8);
    drawBoat(lc, e.b, e.P, e.X, t, knock);
  });
  ctx.save(); ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.globalAlpha = a; ctx.drawImage(L, 0, 0); ctx.restore();
  live.forEach((e) => drawWake(ctx, e.b, e.P, e.X, t, a * (e.b.L > 24 ? 1 : 0.7)));
  // the reflection: the layer, upside down, wobbling, in the top few units of the bar only (the numbers are never drawn over)
  const sd = tf.d, ys = SURF * sd + tf.f, depth = Math.min(6, Math.max(0, o.maxBelow)) * sd, W = L.width;
  if (live.length && a > 0.05 && depth > 0) {
    ctx.save(); ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.beginPath(); ctx.rect(0, ys, W, depth); ctx.clip();
    const step = Math.max(1, Math.round(sd * 0.8));
    live.forEach((e) => {
      const xr0 = Math.max(0, (e.X - e.b.L) * tf.a + tf.e), xr1 = Math.min(W, (e.X + e.b.L) * tf.a + tf.e);
      if (!(xr1 > xr0)) return;
      for (let yy = 0; yy < depth; yy += step) {
        const src = ys - yy - step, wob = Math.sin(yy / sd * 0.9 + t * 2.3) * 0.9 * sd;
        ctx.globalAlpha = a * 0.3 * (1 - yy / depth);
        ctx.drawImage(L, xr0, src, xr1 - xr0, step, xr0 + wob, ys + yy, xr1 - xr0, step);
      }
    });
    ctx.restore();
  }
  ctx.restore();   // the clip
}

export { BARB as SAILBOATS_BARB };
